import { useMutation, useQueryClient } from '@tanstack/react-query';

import apiClient from '@/constants/http';
import { storage, storageKeys } from '@/constants/storage';
import { Logger } from '@/utils/logging';
import { buildTokenPair } from '@/utils/sessionUtils';
import { Tracker } from '@/utils/tracking';

import {
  type ApiError,
  type CodeLoginRequest,
  type CodeLoginResponse,
  NETWORK_ERROR_CODE,
  type UseCodeLoginMutationOptions,
} from './types';

const codeLogin = async (
  loginData: CodeLoginRequest,
  logFailure: boolean,
): Promise<CodeLoginResponse> => {
  try {
    const { data } = await apiClient.post<CodeLoginResponse>(
      '/v1/code-login?include=customer.photos',
      loginData,
    );

    if (data.user.enabled !== false) {
      await apiClient.setTokens(data);
      const tokenPair = buildTokenPair(data);
      if (tokenPair) {
        apiClient.updateTokenPair(tokenPair);
      }
      storage.set(storageKeys.customerId, data.user.customer.id);
    }

    return data;
  } catch (error: unknown) {
    const err = error as { code?: string; message?: string; details?: unknown };
    const isAuthError = err?.code === 'AUTH_CODE_ERROR';

    const apiError: ApiError = {
      message: err?.message || 'Sign in failed',
      // The API client rejects with the response body, so a codeless rejection
      // never reached the server: calling that an invalid code would tell a
      // client their correct code is wrong.
      code: isAuthError ? 'INVALID_CODE' : err?.code || NETWORK_ERROR_CODE,
      details: err?.details,
    };

    // Only the code is logged: the body names the profile fields the server is
    // missing, and the loggers ship to Sentry and Datadog.
    if (logFailure) {
      Logger.error('Error logging in with code', { code: apiError.code });
    }

    throw apiError;
  }
};

export const useCodeLoginMutation = ({
  trackFailure = true,
  logFailure = true,
  ...options
}: UseCodeLoginMutationOptions = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    retry:
      typeof options.retry === 'number'
        ? Math.min(options.retry, 2)
        : (options.retry ?? 2),
    mutationFn: (loginData: CodeLoginRequest) =>
      codeLogin(loginData, logFailure),
    onMutate: async () => {
      const previousCustomerData = queryClient.getQueryData(['customer']);
      return { previousCustomerData };
    },
    onSuccess: (data, variables, context, mutationContext) => {
      if (data.user.enabled !== false) {
        queryClient.invalidateQueries({ queryKey: ['customer'] });
        Tracker.track('sign_in_success', {
          customerId: data.user.customer.id,
          method: 'otp',
        });
      }

      options.onSuccess?.(data, variables, context, mutationContext);
    },
    onError: (error, variables, context, mutationContext) => {
      if (
        context &&
        typeof context === 'object' &&
        'previousCustomerData' in context
      ) {
        queryClient.setQueryData(['customer'], context.previousCustomerData);
      }

      if (trackFailure) {
        Tracker.track('sign_in_failed', {
          method: 'otp',
          error: (error as ApiError).code,
        });
      }

      options.onError?.(error, variables, context, mutationContext);
    },
  });
};
